import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SystemSettingsPanel = () => {
  const [settings, setSettings] = useState({
    workflowAutoSave: true,
    notificationEmail: true,
    notificationSMS: false,
    defaultWorkHours: '8',
    timeZone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD',
    autoBackup: true,
    backupFrequency: 'daily',
    sessionTimeout: '30',
    maxConcurrentUsers: '100',
    enableAuditLog: true,
    dataRetentionDays: '365'
  });

  const [integrationStatus, setIntegrationStatus] = useState({
    erp: { connected: true, lastSync: new Date(Date.now() - 15 * 60000), status: 'healthy' },
    mes: { connected: true, lastSync: new Date(Date.now() - 5 * 60000), status: 'healthy' },
    equipment: { connected: true, lastSync: new Date(Date.now() - 2 * 60000), status: 'warning' }
  });

  const timeZoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' }
  ];

  const backupFrequencyOptions = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' }
  ];

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = () => {
    console.log('Saving settings:', settings);
  };

  const handleTestConnection = (system) => {
    console.log('Testing connection for:', system);
  };

  const formatLastSync = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    return `${Math.floor(diff / 60)}h ago`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'var(--color-success)';
      case 'warning':
        return 'var(--color-warning)';
      case 'error':
        return 'var(--color-error)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return 'CheckCircle2';
      case 'warning':
        return 'AlertTriangle';
      case 'error':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 md:mb-6">General Settings</h3>
          <div className="space-y-4">
            <Checkbox
              label="Enable workflow auto-save"
              description="Automatically save workflow changes every 5 minutes"
              checked={settings?.workflowAutoSave}
              onChange={(e) => handleSettingChange('workflowAutoSave', e?.target?.checked)}
            />
            <Input
              label="Default work hours per day"
              type="number"
              value={settings?.defaultWorkHours}
              onChange={(e) => handleSettingChange('defaultWorkHours', e?.target?.value)}
              min="1"
              max="24"
            />
            <Select
              label="Time zone"
              options={timeZoneOptions}
              value={settings?.timeZone}
              onChange={(value) => handleSettingChange('timeZone', value)}
            />
            <Select
              label="Date format"
              options={dateFormatOptions}
              value={settings?.dateFormat}
              onChange={(value) => handleSettingChange('dateFormat', value)}
            />
            <Input
              label="Session timeout (minutes)"
              type="number"
              value={settings?.sessionTimeout}
              onChange={(e) => handleSettingChange('sessionTimeout', e?.target?.value)}
              min="5"
              max="120"
            />
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 md:mb-6">Notification Settings</h3>
          <div className="space-y-4">
            <Checkbox
              label="Email notifications"
              description="Receive workflow updates via email"
              checked={settings?.notificationEmail}
              onChange={(e) => handleSettingChange('notificationEmail', e?.target?.checked)}
            />
            <Checkbox
              label="SMS notifications"
              description="Receive critical alerts via SMS"
              checked={settings?.notificationSMS}
              onChange={(e) => handleSettingChange('notificationSMS', e?.target?.checked)}
            />
            <div className="pt-4 border-t border-border">
              <h4 className="text-sm font-medium text-foreground mb-3">Notification Rules</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm text-foreground">Production line alerts</span>
                  <span className="caption text-success">Enabled</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm text-foreground">Quality control flags</span>
                  <span className="caption text-success">Enabled</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm text-foreground">Resource allocation changes</span>
                  <span className="caption text-muted-foreground">Disabled</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 md:mb-6">Backup & Recovery</h3>
          <div className="space-y-4">
            <Checkbox
              label="Enable automatic backups"
              description="Automatically backup system data"
              checked={settings?.autoBackup}
              onChange={(e) => handleSettingChange('autoBackup', e?.target?.checked)}
            />
            <Select
              label="Backup frequency"
              options={backupFrequencyOptions}
              value={settings?.backupFrequency}
              onChange={(value) => handleSettingChange('backupFrequency', value)}
              disabled={!settings?.autoBackup}
            />
            <Input
              label="Data retention period (days)"
              type="number"
              value={settings?.dataRetentionDays}
              onChange={(e) => handleSettingChange('dataRetentionDays', e?.target?.value)}
              min="30"
              max="3650"
            />
            <div className="pt-4 border-t border-border">
              <Button variant="outline" fullWidth iconName="Download" iconPosition="left">
                Download Manual Backup
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 md:mb-6">Security & Compliance</h3>
          <div className="space-y-4">
            <Checkbox
              label="Enable audit logging"
              description="Track all system changes and user actions"
              checked={settings?.enableAuditLog}
              onChange={(e) => handleSettingChange('enableAuditLog', e?.target?.checked)}
            />
            <Input
              label="Maximum concurrent users"
              type="number"
              value={settings?.maxConcurrentUsers}
              onChange={(e) => handleSettingChange('maxConcurrentUsers', e?.target?.value)}
              min="10"
              max="500"
            />
            <div className="pt-4 border-t border-border">
              <h4 className="text-sm font-medium text-foreground mb-3">Compliance Standards</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg">
                  <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                  <span className="text-sm text-foreground">ISO 9001 Compliant</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg">
                  <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                  <span className="text-sm text-foreground">Lean Manufacturing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-xl border border-border p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h3 className="text-lg font-semibold text-foreground">Integration Status</h3>
          <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left">
            Refresh All
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(integrationStatus)?.map(([key, value]) => (
            <div key={key} className="p-4 bg-muted rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-foreground uppercase">{key}</span>
                <Icon
                  name={getStatusIcon(value?.status)}
                  size={20}
                  color={getStatusColor(value?.status)}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="caption text-muted-foreground">Status</span>
                  <span className={`caption font-medium ${
                    value?.connected ? 'text-success' : 'text-error'
                  }`}>
                    {value?.connected ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="caption text-muted-foreground">Last Sync</span>
                  <span className="caption text-foreground">{formatLastSync(value?.lastSync)}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="Activity"
                  iconPosition="left"
                  onClick={() => handleTestConnection(key)}
                  className="mt-2"
                >
                  Test Connection
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-end gap-3">
        <Button variant="outline" onClick={() => console.log('Reset settings')}>
          Reset to Defaults
        </Button>
        <Button variant="default" iconName="Save" iconPosition="left" onClick={handleSaveSettings}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default SystemSettingsPanel;